import { send } from "@utils/fetch";
import { PatientIntakeSessionStorage } from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/usePatientIntakeSessionStorage";
import { getLocalFormattedDate } from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/getLocalFormattedDate";
import { ConsultType } from "@components/refactored-survey/question-sets/insurance-survey-questions/utils/insuranceConstants";

const getIsRescheduleFromWindowLocation = () =>
  window.location.hash.split("/")[1] === "reschedule";

export const postPatientIntake = (
  userId: string,
  body: PatientIntakeSessionStorage
): Promise<JsonObject> => {
  const url = `/api/insurance/v3/patient_intake/${userId}/`;
  return send("POST", url, body);
};

// On success we get an empty response back
type ScheduleAndFinalizeValidResponse = Record<string, never>;

type ScheduleAndFinalizeInvalidResponse = {
  error: string;
};

export type ScheduleAndFinalizeResponse =
  | ScheduleAndFinalizeValidResponse
  | ScheduleAndFinalizeInvalidResponse;

export const postScheduleAndFinalize = (
  userId: string,
  wheelConsultId: string,
  workflow: string,
  consultType: ConsultType,
  start: string
): Promise<ScheduleAndFinalizeResponse> => {
  const startFormatted = getLocalFormattedDate(start, consultType);
  const isReschedule = getIsRescheduleFromWindowLocation();
  const url = "/api/insurance/v2/schedule_appointment_and_finalize_consult/";
  return send("POST", url, {
    userId,
    start,
    wheelConsultId,
    workflow,
    consultType,
    startFormatted,
    isReschedule,
  });
};

export const postMakeEligible = (userId: string): Promise<JsonObject> => {
  const url = "/api/insurance/v1/make_eligible_for_reimbursement/";
  return send("POST", url, { userId });
};

export type CreateConsultAndGetTimeslotsResponse = {
  wheelConsultId: string;
  workflow: string;
  consultType: ConsultType;
  timeslots: string[];
};

export const postCreateConsultAndGetTimeslots = (
  userId: string
): Promise<CreateConsultAndGetTimeslotsResponse> => {
  const isReschedule = getIsRescheduleFromWindowLocation();
  return send("POST", "/api/insurance/v2/create_consult_and_get_timeslots/", {
    userId,
    isReschedule,
  });
};

export const getLatestConsultInfo = (userId: string): Promise<JsonObject> =>
  send(
    "GET",
    `/api/insurance/v1/latest_consult_info/${encodeURIComponent(userId)}/`
  );

export enum ConsentType {
  PRIVACY = "PRIVACY",
  TELEHEALTH_INFORMED_CONSENT = "TELEHEALTH_INFORMED_CONSENT",
  RPM_COPAYS = "RPM_COPAYS",
  SMS = "SMS",
}

export type Consent = {
  consent_type: ConsentType;
  consent_status: boolean;
  consent_message: string;
  meristem_sha: string;
  url_to_file: string;
  ip_address?: string;
};

export const postConsentsForTelehealth = (
  user_id: string,
  consent_payloads: Consent[]
): Promise<JsonObject> => {
  const url = `/api/insurance/v1/consent/${user_id}/`;
  return send("POST", url, {
    data: consent_payloads,
  });
};

export type GetConsentForUserResponse = {
  date_consented?: string;
  consent_status?: boolean;
};

export const getConsentForUser = (
  user_id: string,
  consent_type: ConsentType
): Promise<GetConsentForUserResponse> => {
  const url = `/api/insurance/v1/consent/${user_id}/?type=${consent_type}`;
  return send("GET", url);
};

export type GetPhoneNumberResponse = {
  phone_code: string;
};

export const getPhoneCodeForUser = (
  user_id: string
): Promise<GetPhoneNumberResponse> => {
  const url = `/api/insurance/v1/get_phone_code/${user_id}/`;
  return send("GET", url);
};

export type AvailableAppointmentsCountResponse = {
  count: number;
};

export const getAvailableAppointmentsCount = (
  stateCode: string
): Promise<AvailableAppointmentsCountResponse> =>
  send("GET", `/api/insurance/v1/available_appointments_count/${stateCode}/`);

export const getAvailableAppointmentsCountUser = (
  userId: string
): Promise<AvailableAppointmentsCountResponse> =>
  send("GET", `/api/insurance/v1/available_appointments_count_user/${userId}/`);
